"use client";
import { FC, useEffect, useState } from "react";
import { IBanks, IBaseLabelValue } from "@/interfaces";
import { useGetBanks } from "@/hooks";
import {
  findBankByCardNumber,
  findBankByIban,
  formatCardNumberGroups,
  formatIbanGroups,
  hexToRgba,
} from "@/lib";
import Image from "next/image";
import cardBg from "@public/creditCardBg.png";
import Logo from "../logo";
import logoImage from "@public/svg/logo-2.svg";
import { QRCodeCanvas } from "qrcode.react";

interface IProps {
  // Page title and branding
  subtitle: string;
  code?: string;
  // Main display component (credit card, plate, etc.)
  displayComponent?: {
    variant: "credit" | "iban" | "account";
    cardNumber?: string;
    mainValue: {
      label: string;
      value: string;
    };
    headerRight?: {
      label: string;
      value: string;
    };
    ownerName?: string;
    bankName?: string;
    additionalFields?: {
      label: string;
      value: string;
    }[];
    customContent?: React.ReactNode;
  };

  // Result data
  resultContent?: IBaseLabelValue[];

  // Styling options
  accentColor?: string;
  showResultSection?: boolean;
}

const DetailInquiryPdf: FC<IProps> = ({
  subtitle,
  displayComponent,
  resultContent,
  accentColor = "#5a4bd2",
  showResultSection = true,
  code,
}) => {
  const { data: bankOptions } = useGetBanks();
  const [bankInfo, setBankInfo] = useState<IBanks | null>(null);

  useEffect(() => {
    const findBank = displayComponent?.variant.includes("iban")
      ? findBankByIban
      : findBankByCardNumber;
    const valueToFind = displayComponent?.variant.includes("account")
      ? displayComponent.cardNumber
      : displayComponent?.mainValue.value;
    if (!valueToFind) return;

    const result = findBank(valueToFind, bankOptions?.data);
    setBankInfo(result || null);
  }, [
    displayComponent?.mainValue.value,
    bankOptions,
    displayComponent?.variant,
  ]);

  // Determine colors and styling based on component type
  const getComponentColor = () => {
    if (bankInfo?.color) return bankInfo.color;
    return accentColor;
  };

  const componentColor = getComponentColor();

  // Create background style based on component type
  const getBackgroundStyle = () => {
    if (bankInfo) {
      return {
        background: `
              radial-gradient(
              circle at 10% 30%,
              ${hexToRgba(bankInfo.color, 0.4)} 0%,
              ${hexToRgba(bankInfo.color, 0.06)} 40%,
              ${hexToRgba(bankInfo.color, 0.12)} 70%,
              ${hexToRgba(bankInfo.color, 0.3)} 100%
            )`,
      };
    }
    return {
      background: `linear-gradient(135deg, ${hexToRgba(componentColor, 0.08)} 0%, ${hexToRgba(componentColor, 0.03)} 100%)`,
    };
  };

  return (
    <div
      style={{
        backgroundColor: "white",
        padding: "1.5rem",
        position: "relative",
        fontFamily: "IRANYekan, sans-serif",
        direction: "rtl",
        border: "2px solid #5a4bd2",
        borderRadius: "12px",
        margin: "1rem",
        boxSizing: "border-box",
      }}
    >
      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "2rem" }}>
        <div
          style={{
            marginBottom: "0.5rem",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "0.5rem",
          }}
        >
          <Logo width={30} height={30} src={logoImage} className="mt-6" />
          <span
            style={{ fontSize: "1.25rem", fontWeight: "bold", lineHeight: 1 }}
          >
            سامانه استعلام نوین
          </span>
        </div>
        <p
          style={{
            fontWeight: "bold",
            color: "#6A7282",
            marginBottom: "0.5rem",
            fontSize: "15px",
            margin: "0",
          }}
        >
          {subtitle}
        </p>
      </div>
      <div
        style={{
          minHeight: "800px",
          height: "800px",
        }}
      >
        {/* Main Display Component */}
        {displayComponent && (
          <div
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "400px",
              margin: "0 auto 1.5rem",
              borderRadius: "12px",
              padding: "1.5rem 0",
              ...getBackgroundStyle(),
            }}
          >
            {/* Card Background Pattern */}
            <div style={{ position: "absolute", inset: 0, zIndex: 10 }}>
              <Image
                src={cardBg}
                alt={bankInfo?.label || ""}
                className="h-full w-full rounded-lg object-cover"
              />
            </div>

            {/* Header with bank/entity gas-bill-info */}
            {displayComponent.bankName && (
              <div
                style={{
                  marginBottom: ".5rem",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "0 2rem",
                  position: "relative",
                  zIndex: 50,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "0.5rem",
                  }}
                >
                  {bankInfo?.logo ? (
                    <img
                      src={bankInfo.logo}
                      alt={bankInfo.label}
                      style={{
                        width: "32px",
                        height: "32px",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: "32px",
                        height: "32px",
                        backgroundColor: componentColor,
                        borderRadius: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <span style={{ color: "white", fontSize: "12px" }}>
                        {displayComponent.variant.includes("credit")
                          ? "💳"
                          : "📋"}
                      </span>
                    </div>
                  )}
                  <span
                    style={{
                      color: componentColor,
                      fontSize: "1rem",
                      fontWeight: "500",
                      marginBottom: "20px",
                    }}
                  >
                    {bankInfo
                      ? bankInfo.label
                      : displayComponent.bankName || "نامشخص"}
                  </span>
                </div>
                {displayComponent.headerRight && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-end",
                    }}
                  >
                    <span style={{ fontSize: "12px", fontWeight: "300" }}>
                      {displayComponent.headerRight.label}
                    </span>
                    <span
                      style={{ fontSize: "14px", fontWeight: "600" }}
                      dir="ltr"
                    >
                      {displayComponent.headerRight.value}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Main Content */}
            <div
              style={{
                position: "relative",
                zIndex: 50,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                padding: "0 2rem",
              }}
            >
              {/* Main Value Display */}
              <div
                style={{
                  width:
                    displayComponent.variant.includes("credit") ||
                    displayComponent.variant.includes("account")
                      ? "80%"
                      : "100%",
                  margin: "0 auto",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                }}
              >
                <div
                  style={{
                    color: componentColor,
                    fontSize: "12px",
                    fontWeight: "300",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.25rem",
                    justifyContent: "flex-end",
                  }}
                >
                  {displayComponent.mainValue.label}
                </div>
                <p
                  style={{
                    color: componentColor,
                    fontSize: "20px",
                    fontWeight: "bold",
                    direction: "ltr",
                    wordSpacing: displayComponent.variant.includes("iban")
                      ? "4px"
                      : "14px",
                    margin: "0.25rem 0 ",
                  }}
                >
                  {displayComponent.variant.includes("credit")
                    ? formatCardNumberGroups(displayComponent.mainValue.value)
                    : displayComponent.variant.includes("iban")
                      ? formatIbanGroups(displayComponent.mainValue.value)
                      : displayComponent.mainValue.value}
                </p>
              </div>

              {/* Owner Name */}
              {displayComponent.ownerName && (
                <p
                  style={{
                    textAlign: "center",
                    fontSize: "16px",
                    fontWeight: "600",
                    color: "#1f2937",
                    margin: "0 0 0.75rem 0",
                  }}
                >
                  {displayComponent.ownerName}
                </p>
              )}

              {/* Additional Fields */}
              {displayComponent.additionalFields &&
                displayComponent.additionalFields.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      width: "100%",
                      justifyContent: "space-between",
                      padding: "0 1rem",
                      flexWrap: "wrap",
                      gap: "0.75rem",
                    }}
                  >
                    {displayComponent.additionalFields.map((field, index) => (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          minWidth: "120px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "12px",
                            fontWeight: "300",
                          }}
                        >
                          {field.label}
                        </span>
                        <span
                          style={{
                            fontSize: "14px",
                            fontWeight: "600",
                            color: "#1f2937",
                          }}
                        >
                          {field.value}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

              {/* Custom Content */}
              {displayComponent.customContent && (
                <div style={{ margin: "1rem 0" }}>
                  {displayComponent.customContent}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Result Content */}
        {showResultSection && resultContent && resultContent.length > 0 && (
          <div
            style={{
              marginBottom: "2.5rem",
              marginTop: "1rem",
              display: "flex",
              flexDirection: "column",
              gap: "1.5rem",
              borderRadius: "12px",
              padding: "1rem 2rem",
              maxWidth: "500px",
              margin: "auto",
            }}
          >
            {resultContent.map((item, index) => (
              <div
                key={index}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "1.5rem",
                  fontSize: "14px",
                  justifyContent: "space-between",
                }}
              >
                <span style={{ minWidth: "max-content" }}>{item.label}</span>
                <span style={{ color: "#5a4bd2", textAlign: "start" }}>
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div
        style={{
          width: "100%",
          marginTop: "auto",
          position: "relative",
          zIndex: 50,
        }}
      >
        <hr />
        <footer
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
            padding: "1rem",
          }}
        >
          <p style={{ margin: 0, fontSize: "14px", lineHeight: "30px" }}>
            این گزارش توسط سامانه استعلامات نوین وان (novin.one) تولید شده است.
            <br />
            اعتبار این استعلام را می‌توانید در بخش <b>
              "تأیید اصالت گزارش"
            </b>{" "}
            سایت بررسی کنید.
          </p>
          {code && (
            <div style={{ display: "flex" }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <span style={{ color: "#AAAAAA", fontSize: "14px" }}>
                  شناسه اصالت گزارش
                </span>
                <span
                  className="text-en"
                  style={{
                    color: "#D32F2F",
                    fontWeight: "bold",
                    fontSize: "26px",
                    letterSpacing: "1px",
                  }}
                >
                  {code}
                </span>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  position: "relative",
                }}
              >
                <div style={{ width: "100px", height: "100px" }}>
                  <QRCodeCanvas
                    value={`https://novin.one/inquiry-validity?code=${code}`}
                    size={100}
                    level="H"
                    marginSize={3}
                  />
                </div>
                <div
                  style={{
                    position: "absolute",
                    left: "-40px",
                    top: "50%",
                    transform: "translateY(-50%) rotate(-90deg)",
                    transformOrigin: "center center",
                    fontSize: "12px",
                    color: "#AAAAAA",
                    whiteSpace: "nowrap",
                  }}
                >
                  اسکن کنید
                </div>
              </div>
            </div>
          )}
        </footer>
      </div>
    </div>
  );
};

export default DetailInquiryPdf;
